import type {
  AppearanceOverrides,
  CharacterSignalSnapshot,
  HoldingSnapshot,
  Scenario,
  ScenarioId,
} from "./types";

const FARM_UNIT = 15_000_000;
const PORTFOLIO_ID = "demo-kys-farm";
const PREVIOUS_MARKET_DATES: Readonly<Record<string, string>> = {
  "2026-07-24": "2026-07-23",
  "2026-07-25": "2026-07-24",
  "2026-07-28": "2026-07-27",
  "2026-07-29": "2026-07-28",
};

const POSITION_META: Record<
  string,
  { quantity: number; costBasis: number }
> = {
  "NASDAQ:AAPL": { quantity: 100, costBasis: 26_400_000 },
  "NASDAQ:NVDA": { quantity: 80, costBasis: 20_800_000 },
  "NASDAQ:MSFT": { quantity: 60, costBasis: 18_600_000 },
  "NYSEARCA:SPY": { quantity: 30, costBasis: 14_400_000 },
  "NASDAQ:TSLA": { quantity: 40, costBasis: 10_800_000 },
  "NYSE:KO": { quantity: 100, costBasis: 6_300_000 },
};

function holding(
  instrumentId: string,
  ticker: string,
  name: string,
  marketValue: number,
  dailyReturnPct: number,
  characterSignals?: CharacterSignalSnapshot,
): HoldingSnapshot {
  const meta = POSITION_META[instrumentId];
  const quantity = meta?.quantity ?? 1;
  const costBasis = meta?.costBasis ?? marketValue;
  const previousValue = marketValue / (1 + dailyReturnPct / 100);

  return {
    instrumentId,
    ticker,
    name,
    marketValue,
    dailyReturnPct,
    quoteStatus: "valid",
    quantity,
    averagePrice: costBasis / quantity,
    currentPrice: marketValue / quantity,
    costBasis,
    dailyChangeAmount: marketValue - previousValue,
    ...(characterSignals ? { characterSignals } : {}),
  };
}

function portfolio(
  marketDate: string,
  asOf: string,
  positions: readonly HoldingSnapshot[],
) {
  return {
    portfolioId: PORTFOLIO_ID,
    marketDate,
    previousMarketDate: PREVIOUS_MARKET_DATES[marketDate] ?? marketDate,
    asOf,
    currency: "KRW",
    farmUnit: FARM_UNIT,
    positions,
  } as const;
}

const sharedOverrides: AppearanceOverrides = {
  "NASDAQ:AAPL": { paletteId: "leaf", patternId: "plain" },
  "NASDAQ:NVDA": { paletteId: "berry", patternId: "plain" },
  "NASDAQ:MSFT": {
    paletteId: "honey",
    patternId: "plain",
    hatId: "hat.cap.blue",
  },
  "NYSEARCA:SPY": {
    paletteId: "mint",
    patternId: "plain",
    hatId: "hat.straw",
  },
  "NASDAQ:TSLA": { paletteId: "apricot", patternId: "plain" },
  "NYSE:KO": {
    paletteId: "violet",
    patternId: "plain",
    hatId: "hat.ribbon.red",
  },
};

export const SCENARIOS: readonly Scenario[] = [
  {
    id: "sunny",
    name: "평온한 맑음",
    title: "평온한 맑음",
    shortLabel: "맑음",
    description:
      "대부분 살짝 올라 햇살이 따뜻합니다. KO 슬라임만 조금 시무룩해요.",
    accent: "#E1A928",
    portfolio: portfolio("2026-07-24", "2026-07-24T15:30:00+09:00", [
      holding("NASDAQ:AAPL", "AAPL", "Apple", 32_000_000, 0.8, {
        returnStreakDays: 3,
        volatilityPct: 1.1,
        drawdownPct: 0.8,
      }),
      holding("NASDAQ:NVDA", "NVDA", "NVIDIA", 27_000_000, 1.4, {
        returnStreakDays: 6,
        volatilityPct: 2.1,
      }),
      holding("NASDAQ:MSFT", "MSFT", "Microsoft", 20_000_000, 0.04, {
        flatStreakDays: 4,
      }),
      holding("NYSEARCA:SPY", "SPY", "SPDR S&P 500 ETF", 15_000_000, 0.35),
      holding("NASDAQ:TSLA", "TSLA", "Tesla", 9_000_000, 1.4),
      holding("NYSE:KO", "KO", "Coca-Cola", 6_000_000, -0.6, {
        returnStreakDays: -2,
      }),
    ]),
    appearanceOverrides: sharedOverrides,
  },
  {
    id: "party",
    name: "수익 파티",
    title: "수익 파티",
    shortLabel: "파티",
    description:
      "기술주가 크게 뛰어 농장이 파티장이 됩니다. KO는 혼자 하락 중이에요.",
    accent: "#F06A53",
    portfolio: portfolio("2026-07-25", "2026-07-25T15:30:00+09:00", [
      holding("NASDAQ:AAPL", "AAPL", "Apple", 34_000_000, 3.8, {
        returnStreakDays: 3,
        volatilityPct: 1.8,
      }),
      holding("NASDAQ:NVDA", "NVDA", "NVIDIA", 29_000_000, 7.2),
      holding("NASDAQ:MSFT", "MSFT", "Microsoft", 21_000_000, 2.4, {
        returnStreakDays: 6,
      }),
      holding("NYSEARCA:SPY", "SPY", "SPDR S&P 500 ETF", 15_000_000, 1.8),
      holding("NASDAQ:TSLA", "TSLA", "Tesla", 10_000_000, 15.4),
      holding("NYSE:KO", "KO", "Coca-Cola", 6_000_000, -1.1, {
        returnStreakDays: -3,
      }),
    ]),
    appearanceOverrides: {
      ...sharedOverrides,
      "NASDAQ:NVDA": {
        ...sharedOverrides["NASDAQ:NVDA"],
        hatId: "hat.party.purple",
      },
    },
  },
  {
    id: "moonshot",
    name: "문샷 축제",
    title: "문샷 축제",
    shortLabel: "문샷",
    description:
      "포트폴리오가 수직 상승해 코인비와 디스코 조명이 농장을 뒤덮습니다.",
    accent: "#FFD65A",
    portfolio: portfolio("2026-07-25", "2026-07-25T15:31:00+09:00", [
      holding("NASDAQ:AAPL", "AAPL", "Apple", 35_000_000, 7.8, {
        returnStreakDays: 7,
        volatilityPct: 2.6,
      }),
      holding("NASDAQ:NVDA", "NVDA", "NVIDIA", 31_000_000, 13.4, {
        returnStreakDays: 9,
      }),
      holding("NASDAQ:MSFT", "MSFT", "Microsoft", 22_000_000, 6.6, {
        returnStreakDays: 6,
      }),
      holding("NYSEARCA:SPY", "SPY", "SPDR S&P 500 ETF", 16_000_000, 4.2),
      holding("NASDAQ:TSLA", "TSLA", "Tesla", 11_000_000, 19.8),
      holding("NYSE:KO", "KO", "Coca-Cola", 6_000_000, -0.2),
    ]),
    appearanceOverrides: {
      ...sharedOverrides,
      "NASDAQ:NVDA": {
        ...sharedOverrides["NASDAQ:NVDA"],
        hatId: "hat.party.purple",
      },
      "NASDAQ:TSLA": {
        ...sharedOverrides["NASDAQ:TSLA"],
        hatId: "hat.party.purple",
      },
    },
  },
  {
    id: "storm",
    name: "거센 소나기",
    title: "거센 소나기",
    shortLabel: "폭풍",
    description:
      "포트폴리오는 크게 젖었지만 NVDA 슬라임은 홀로 신나게 뛰어다닙니다.",
    accent: "#5B729F",
    portfolio: portfolio("2026-07-28", "2026-07-28T15:30:00+09:00", [
      holding("NASDAQ:AAPL", "AAPL", "Apple", 30_000_000, -3.2),
      holding("NASDAQ:NVDA", "NVDA", "NVIDIA", 25_000_000, 1.1),
      holding("NASDAQ:MSFT", "MSFT", "Microsoft", 19_000_000, -2.7),
      holding(
        "NYSEARCA:SPY",
        "SPY",
        "SPDR S&P 500 ETF",
        14_000_000,
        -2.1,
        { volatilityPct: 5.3, drawdownPct: 4.2 },
      ),
      holding("NASDAQ:TSLA", "TSLA", "Tesla", 8_000_000, -5.2, {
        volatilityPct: 10.4,
      }),
      holding("NYSE:KO", "KO", "Coca-Cola", 6_000_000, -0.8),
    ]),
    appearanceOverrides: sharedOverrides,
  },
  {
    id: "panic",
    name: "시장 패닉",
    title: "시장 패닉",
    shortLabel: "패닉",
    description:
      "번개와 불티가 농장을 뒤덮습니다. 방어주 KO만 작은 미소를 지어요.",
    accent: "#D94E39",
    portfolio: portfolio("2026-07-29", "2026-07-29T15:30:00+09:00", [
      holding("NASDAQ:AAPL", "AAPL", "Apple", 28_000_000, -7.2),
      holding("NASDAQ:NVDA", "NVDA", "NVIDIA", 22_000_000, -9.8, {
        returnStreakDays: -4,
        drawdownPct: 14,
      }),
      holding("NASDAQ:MSFT", "MSFT", "Microsoft", 18_000_000, -5.4, {
        volatilityPct: 10.8,
      }),
      holding("NYSEARCA:SPY", "SPY", "SPDR S&P 500 ETF", 13_000_000, -4.1),
      holding("NASDAQ:TSLA", "TSLA", "Tesla", 7_000_000, -12.6),
      holding("NYSE:KO", "KO", "Coca-Cola", 6_000_000, 0.7),
    ]),
    appearanceOverrides: sharedOverrides,
  },
  {
    id: "catastrophe",
    name: "시장 대재난",
    title: "시장 대재난",
    shortLabel: "대재난",
    description:
      "폭우와 침수, 강풍 잔해가 농장을 뒤덮은 극단적 폭락 장면입니다. KO만 간신히 버팁니다.",
    accent: "#8B211D",
    portfolio: portfolio("2026-07-29", "2026-07-29T15:31:00+09:00", [
      holding("NASDAQ:AAPL", "AAPL", "Apple", 27_000_000, -11.4, {
        returnStreakDays: -6,
        drawdownPct: 18,
      }),
      holding("NASDAQ:NVDA", "NVDA", "NVIDIA", 20_000_000, -16.8, {
        returnStreakDays: -5,
        drawdownPct: 24,
      }),
      holding("NASDAQ:MSFT", "MSFT", "Microsoft", 17_000_000, -9.2),
      holding("NYSEARCA:SPY", "SPY", "SPDR S&P 500 ETF", 12_000_000, -7.6, {
        volatilityPct: 13.2,
        drawdownPct: 11,
      }),
      holding("NASDAQ:TSLA", "TSLA", "Tesla", 6_000_000, -22.4, {
        volatilityPct: 28,
      }),
      holding("NYSE:KO", "KO", "Coca-Cola", 6_000_000, 0.1),
    ]),
    appearanceOverrides: sharedOverrides,
  },
] as const;

export const DEFAULT_SCENARIO_ID: ScenarioId = "sunny";

export function getScenario(id: ScenarioId | string): Scenario {
  return (
    SCENARIOS.find((scenario) => scenario.id === id) ??
    SCENARIOS.find((scenario) => scenario.id === DEFAULT_SCENARIO_ID) ??
    SCENARIOS[0]
  );
}
